/**
 * Special Card Viewer
 * A special card full size (opened by a long-press on the card). Tap the
 * card to turn it over: the back is the gold Special card with the card's
 * name, whether it helps or hinders, and its effect. Tap outside to close.
 */

import React, { useRef, useState } from 'react';
import { SPECIAL_CARD_INFO, SpecialCardSign, type SpecialCardType } from '@pokercubed/shared';
import { SPECIAL_BACK_ART, SPECIAL_BLANK_ART, SPECIAL_CARD_ART } from '../../assets/cards';
import { SPECIAL_CARD_TEXT } from './specialCardText';

/** Shown for a special not turned over: your own until the 4 corners, an opponent's until a showdown. */
const UNREVEALED_TEXT =
  "A special card — it could Help or Hinder. Its holder sees which once the 4 corners are revealed; everyone else only at a showdown (or with a Spy).";

interface SpecialCardViewerProps {
  special: SpecialCardType | 'unrevealed';
  onClose: () => void;
}

const SpecialCardViewer: React.FC<SpecialCardViewerProps> = ({ special, onClose }) => {
  const unrevealed = special === 'unrevealed';
  const info = unrevealed ? null : SPECIAL_CARD_INFO[special];
  const art = unrevealed ? SPECIAL_BACK_ART : SPECIAL_CARD_ART[special] ?? SPECIAL_BLANK_ART;
  const [flipped, setFlipped] = useState(false);
  // The long-press that opened this ends with a lift that can land as a
  // click on the card: ignore clicks in the first moment.
  const openedAt = useRef(Date.now());
  const face = 'absolute inset-0 [backface-visibility:hidden] [-webkit-backface-visibility:hidden]';

  return (
    <div
      data-testid="special-card-viewer"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-black/80 p-4"
      onPointerDown={onClose}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={flipped ? 'Turn the card back over' : 'Turn the card over to read its effect'}
        data-testid="special-card-viewer-card"
        className="relative aspect-[300/422] w-[min(86vw,calc(78dvh*300/422),380px)] cursor-pointer select-none [perspective:1200px] [container-type:inline-size]"
        style={{ WebkitTouchCallout: 'none' }}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => {
          if (Date.now() - openedAt.current > 350) setFlipped((f) => !f);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setFlipped((f) => !f);
          } else if (e.key === 'Escape') onClose();
        }}
      >
        <div
          className="relative h-full w-full transition-transform duration-500 [transform-style:preserve-3d] motion-reduce:transition-none"
          style={{ transform: flipped ? 'rotateY(180deg)' : 'none' }}
        >
          {/* Front: the card's own art. */}
          <img src={art} alt={info?.name ?? 'Special'} draggable={false} className={`${face} h-full w-full object-contain`} />

          {/* Back: the gold Special card with the effect laid over its "?". */}
          <div className={`${face} [transform:rotateY(180deg)]`} data-testid="special-card-viewer-back">
            <img src={SPECIAL_BACK_ART} alt="" draggable={false} className="absolute inset-0 h-full w-full object-contain" />
            <div className="absolute left-[11%] right-[11%] top-[23%] bottom-[22%] flex flex-col items-center justify-center gap-[3cqw] rounded-[4cqw] border-[0.6cqw] border-amber-300/70 bg-[#2b0b2e]/[0.93] px-[5cqw] py-[4cqw] text-center shadow-inner">
              <div className="font-serif text-[9cqw] font-bold leading-none tracking-wide text-amber-200">{info?.name ?? 'Special'}</div>
              {info && (
                <span
                  className={`rounded-[2cqw] px-[3cqw] py-[1cqw] text-[4.2cqw] font-bold uppercase tracking-wider text-white ${
                    info.sign === SpecialCardSign.POSITIVE ? 'bg-emerald-600' : 'bg-rose-700'
                  }`}
                >
                  {info.sign === SpecialCardSign.POSITIVE ? 'Help ↑' : 'Hinder ↓'}
                </span>
              )}
              <p className="text-[5.4cqw] leading-snug text-amber-50">{unrevealed ? UNREVEALED_TEXT : SPECIAL_CARD_TEXT[special]}</p>
            </div>
          </div>
        </div>
      </div>
      <p className="text-center text-xs text-gray-400">Tap the card to turn it over · tap outside to close</p>
    </div>
  );
};

export default SpecialCardViewer;
